import { useState, useEffect } from "react";
import { API, axios } from "@/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Zap, Check, Loader2 } from "lucide-react";

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/**
 * DoublePunchConfig — Vendor configuration for Double Punch Days.
 * Max 3 days per week. Renders inside VendorSettings or VendorGoLive.
 */
export default function DoublePunchConfig({ businessId }) {
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!businessId) return;
    axios
      .get(`${API}/punchcard/double-punch-days/${businessId}`)
      .then((res) => setDays(res.data.double_punch_days || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [businessId]);

  const toggleDay = (dayIdx) => {
    setDays((prev) => {
      if (prev.includes(dayIdx)) {
        return prev.filter((d) => d !== dayIdx);
      }
      if (prev.length >= 3) {
        toast.error("Max 3 double punch days per week");
        return prev;
      }
      return [...prev, dayIdx].sort();
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/punchcard/double-punch-days`, {
        business_id: businessId,
        days,
      });
      toast.success("Double Punch Days saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-3">
        <Loader2 className="w-4 h-4 animate-spin text-[#F59E0B]" />
        <span className="text-xs text-white/40">Loading punch settings...</span>
      </div>
    );
  }

  return (
    <div className="bg-[#2C2C2E] rounded-xl p-4 space-y-3" data-testid="double-punch-config">
      <div className="flex items-center gap-2">
        <Zap className="w-4 h-4 text-[#F59E0B]" />
        <p className="text-sm font-bold text-white/80">Double Punch Days</p>
      </div>
      <p className="text-[11px] text-white/40 leading-relaxed">
        Choose up to 3 days per week where customers earn 2x punches. Great for
        slow days to drive traffic.
      </p>

      <div className="flex gap-1.5" data-testid="double-punch-day-selector">
        {DAY_LABELS.map((label, idx) => {
          const active = days.includes(idx);
          return (
            <button
              key={idx}
              onClick={() => toggleDay(idx)}
              className={`flex-1 py-2 rounded-lg text-[10px] font-bold transition-all ${
                active
                  ? "bg-[#F59E0B] text-[#1C1C1E] shadow-[0_0_8px_rgba(245,158,11,0.3)]"
                  : "bg-[#1C1C1E] text-white/40 border border-white/5 hover:border-[#F59E0B]/30"
              }`}
              data-testid={`dpd-${label.toLowerCase()}`}
            >
              {label}
              {active && (
                <span className="block text-[8px] mt-0.5 opacity-70">2x</span>
              )}
            </button>
          );
        })}
      </div>

      <button
        onClick={handleSave}
        disabled={saving}
        className="w-full py-2 rounded-lg bg-[#F59E0B]/10 text-[#F59E0B] text-xs font-bold border border-[#F59E0B]/20 hover:bg-[#F59E0B]/20 transition flex items-center justify-center gap-1.5 disabled:opacity-50"
        data-testid="save-double-punch-days"
      >
        {saving ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : (
          <Check className="w-3.5 h-3.5" />
        )}
        Save Schedule
      </button>
    </div>
  );
}
